import { Injectable, signal, APP_INITIALIZER, inject, EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';

export interface ThemeOption {
  id: string;
  label: string;
  primaryColor: string;
  headerColor: string;
}

export type ColorMode = 'light' | 'dark';

const LEGACY_STORAGE_KEY = 'dental-ui-clinic-theme';
const PALETTE_STORAGE_KEY = 'dental-ui-clinic-palette';
const COLOR_MODE_STORAGE_KEY = 'dental-ui-clinic-color-mode';
const DEFAULT_PALETTE_ID = 'default';

/** Renk paleti — localhost tema seçim panelinde gösterilir */
export const THEME_PALETTE_OPTIONS: ThemeOption[] = [
  { id: 'default', label: 'Varsayılan', primaryColor: '#030213', headerColor: '#042432' },
  { id: 'mint', label: 'Mint', primaryColor: '#8ed2a9', headerColor: '#1a4d2e' },
  { id: 'mavi', label: 'Mavi', primaryColor: '#5ba2ef', headerColor: '#1a4a7a' },
  { id: 'bordo', label: 'Bordo', primaryColor: '#b42c13', headerColor: '#5a1609' },
  { id: 'mor-murdum', label: 'Mor/Mürdüm', primaryColor: '#7a1a4a', headerColor: '#2a0419' },
];

const VALID_PALETTE_IDS = new Set(THEME_PALETTE_OPTIONS.map((t) => t.id));

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly _paletteId = signal<string>(DEFAULT_PALETTE_ID);
  private readonly _colorMode = signal<ColorMode>('light');

  readonly currentPaletteId = this._paletteId.asReadonly();
  readonly colorMode = this._colorMode.asReadonly();

  constructor() {
    this.loadStoredPreferences();
    this.applyTheme();
  }

  /** Temayı başlat - APP_INITIALIZER tarafından çağrılır */
  initialize(): void {
    this.loadStoredPreferences();
    this.applyTheme();
  }

  /** Renk paleti seçimi (localhost panel) */
  setPalette(paletteId: string): void {
    if (!VALID_PALETTE_IDS.has(paletteId)) return;
    this._paletteId.set(paletteId);
    this.persistPalette(paletteId);
    this.applyTheme();
  }

  /** Açık/karanlık mod (Ayarlar ekranı) */
  setColorMode(mode: ColorMode): void {
    if (mode !== 'light' && mode !== 'dark') return;
    this._colorMode.set(mode);
    this.persistColorMode(mode);
    this.applyTheme();
  }

  private loadStoredPreferences(): void {
    const { paletteId, colorMode } = this.readStorage();
    this._paletteId.set(paletteId);
    this._colorMode.set(colorMode);
  }

  private readStorage(): { paletteId: string; colorMode: ColorMode } {
    try {
      const storedPalette = localStorage.getItem(PALETTE_STORAGE_KEY);
      const storedMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);

      if (storedPalette || storedMode) {
        return {
          paletteId:
            storedPalette && VALID_PALETTE_IDS.has(storedPalette)
              ? storedPalette
              : DEFAULT_PALETTE_ID,
          colorMode: storedMode === 'dark' ? 'dark' : 'light',
        };
      }

      return this.migrateLegacyStorage();
    } catch {
      return { paletteId: DEFAULT_PALETTE_ID, colorMode: 'light' };
    }
  }

  /** Eski tek anahtarlı depolamayı yeni yapıya taşır */
  private migrateLegacyStorage(): { paletteId: string; colorMode: ColorMode } {
    try {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!legacy) {
        return { paletteId: DEFAULT_PALETTE_ID, colorMode: 'light' };
      }

      let paletteId = DEFAULT_PALETTE_ID;
      let colorMode: ColorMode = 'light';

      if (legacy === 'dark') {
        colorMode = 'dark';
      } else if (legacy === 'light' || legacy === 'default') {
        colorMode = 'light';
      } else if (VALID_PALETTE_IDS.has(legacy)) {
        paletteId = legacy;
      }

      this.persistPalette(paletteId);
      this.persistColorMode(colorMode);
      localStorage.removeItem(LEGACY_STORAGE_KEY);

      return { paletteId, colorMode };
    } catch {
      return { paletteId: DEFAULT_PALETTE_ID, colorMode: 'light' };
    }
  }

  private persistPalette(paletteId: string): void {
    try {
      localStorage.setItem(PALETTE_STORAGE_KEY, paletteId);
    } catch {
      // ignore
    }
  }

  private persistColorMode(mode: ColorMode): void {
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }

  private applyTheme(): void {
    const root = document.documentElement;

    root.classList.remove('dark');
    root.removeAttribute('data-theme');

    if (this._colorMode() === 'dark') {
      root.classList.add('dark');
    }

    const paletteId = this._paletteId();
    if (paletteId !== DEFAULT_PALETTE_ID) {
      root.setAttribute('data-theme', paletteId);
    }
  }
}

/**
 * Tema sistemini uygulama başlangıcında aktif hale getirir.
 * Tüm uygulamaların app.config.ts dosyasında kullanılmalıdır.
 */
export function provideTheme(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const themeService = inject(ThemeService);
        return () => themeService.initialize();
      },
      multi: true,
    },
  ]);
}
